// src/hooks/useStatuses.js
// Uses rtmp-monitor.json (generated every 2s on each node) for all status data.
import { useCallback, useRef } from 'react';
import { authFetch } from '../utils/authFetch';

const API_BASE =
  (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '')) ||
  'http://localhost:4000';

export function useStatuses({
  setRtmpOk,
  setIngestOk,
  setFfmpegStat,
  setSpeedMap,            // optional
  setIngestKbps,          // optional: { instanceId: kbps_in } — live ingest bitrate for the badge
  setSourceOverrideMap,   // optional: { instanceId: { forcedSource, activeSource } }
  checkRtmp,              // axios func -> /event-wizard/check-rtmp?ip=...
  onInstanceGone,         // optional: called (throttled) when the backend says an instance no longer exists (410)
}) {
  // Guards against out-of-order writes: refreshStatuses is called from the
  // 2s poll, from fetchFull, and from the manual refresh button — these can
  // overlap. A slow older call (e.g. its TCP checks timing out at 1s each)
  // resolving AFTER a fast newer one would overwrite fresh OK badges with
  // stale KO ones. Only the most recently started call gets to write.
  const seqRef = useRef(0);

  // Stable identity: the status poll lists this in its effect deps, so a new
  // function on every render tore the poll down and restarted its timer before it
  // could fire. Everything closed over here is already stable — useState setters
  // by React's guarantee, checkRtmp because it is a useCallback — so there is no
  // stale-closure risk in memoising it.
  // Consecutive TCP-probe misses per instance, for flap damping: the probe
  // crosses the public internet and fails sporadically even against a healthy
  // SRS, and a raw write-per-cycle made the badge blink red/green in a loop.
  const rtmpFailStreakRef = useRef({});
  // Consecutive missed monitor reads per instance — same damping, monitor side.
  const monFailStreakRef = useRef({});
  // Last time we asked the page to reload its instance list because of 410s.
  const goneRefreshAtRef = useRef(0);

  const refreshStatuses = useCallback(async (instances = []) => {
    if (!instances?.length) return;
    const seq = ++seqRef.current;
    const isLatest = () => seq === seqRef.current;

    // Per-request deadline. Without one, a single slow instance (controller
    // blocked in execSync during prepare/archive, box mid-restart) hangs its
    // requests for up to the gateway timeout; the browser's ~6-connections-
    // per-origin cap then queues EVERY later poll behind them and the badges
    // freeze until a page reload. A missed cycle just keeps the previous
    // badge value — far better than freezing all of them.
    // 8s, not 4: the RTMP probe legitimately takes up to ~2.5s of network
    // time (see checkRtmpPort) and the deadline clock starts while the
    // request may still be QUEUED behind the connection cap — 4s turned
    // queue wait into deterministic probe misses and painted SRS red on
    // healthy boxes (seen 2026-10-01). The deadline's job is killing
    // multi-minute hangs, not shaving seconds.
    const DEADLINE_MS = 8000;
    const withDeadline = (p) =>
      Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('poll deadline')), DEADLINE_MS))]);

    // --- RTMP probe (TCP :1935) — collected here, but NOT written yet: the
    // badge gets ONE write per cycle, after merging with the ingest evidence
    // below. Writing the raw probe first and promoting later painted
    // red-then-green on every cycle whenever the probe was failing.
    let rtmpProbe = {};
    try {
      const rtmpArr = await Promise.all(
        instances.map(inst =>
          withDeadline(checkRtmp(inst.publicIp))
            .then(r => [inst.instanceId, !!r.data?.online])
            .catch(() => [inst.instanceId, false])
        )
      );
      rtmpProbe = Object.fromEntries(rtmpArr);
    } catch {}

    // --- MONITOR JSON (ingest + encoder + speed) + source override for non-master instances ---
    const cams = instances.filter(inst => String(inst.role || '').toLowerCase() !== 'master');
    // 410 = the instance was terminated but is still in our (stale) list — the
    // status poll would otherwise keep hammering dead ids every 2s. Collect
    // them and ask the page (throttled) to refresh its instance list, which
    // drops them from the table and stops the polling at the source.
    const goneIds = new Set();
    // One request per cam, with a deadline. The source-override state is no
    // longer fetched per-cam from /proxy/force: the monitor JSON already
    // carries it (proxy.active_source + drift.forced_source, both present
    // since before IR-195) — that fan-out doubled the request count per
    // cycle and ate into the browser's per-origin connection budget.
    const monitorResults = await Promise.all(
      cams.map(async inst => {
        try {
          const url = `${API_BASE}/event-wizard/proxy/${inst.instanceId}/rtmp-monitor`;
          const res = await authFetch(url, { timeoutMs: DEADLINE_MS });
          if (res.status === 410) { goneIds.add(inst.instanceId); return [inst.instanceId, null]; }
          if (!res.ok) return [inst.instanceId, null];
          const j = await res.json();
          return [inst.instanceId, j];
        } catch {
          return [inst.instanceId, null];
        }
      })
    );

    const ingestMap = {};
    const ffmpegMap = {};
    const speedMap = {};
    const kbpsMap = {};
    // Missed monitor reads this cycle: carry the PREVIOUS values instead of
    // painting the instance down. Same flap damping the SRS badge already has
    // below — a single timed-out cycle is routine now that Studio, backend and
    // instances can sit in three different regions (ingest/encoder/kbps badges
    // and the bitrate label blinked red/green on every missed cycle without it).
    const missedIds = new Set();

    for (const [id, mon] of monitorResults) {
      if (!mon) {
        const misses = (monFailStreakRef.current[id] || 0) + 1;
        monFailStreakRef.current[id] = misses;
        if (misses < 2) { missedIds.add(id); continue; }
        ingestMap[id] = false;
        ffmpegMap[id] = false;
        speedMap[id] = '-';
        kbpsMap[id] = null;
        continue;
      }
      monFailStreakRef.current[id] = 0;
      ingestMap[id] = !!(mon.ingest?.active);
      ffmpegMap[id] = !!(mon.encoder?.running) || !!(mon.segments?.active);
      const spd = mon.encoder?.speed;
      speedMap[id] = spd != null ? `${spd}x` : '-';
      kbpsMap[id] = mon.ingest?.kbps_in ?? null;
    }

    if (!isLatest()) return;

    // Keep the previous value for instances whose read was missed this cycle.
    const mergeCarry = (prev, next) => {
      const out = { ...next };
      for (const id of missedIds) if (id in (prev || {})) out[id] = prev[id];
      return out;
    };
    setIngestOk(prev => mergeCarry(prev, ingestMap));
    setFfmpegStat(prev => mergeCarry(prev, ffmpegMap));
    if (setSpeedMap) setSpeedMap(prev => mergeCarry(prev, speedMap));
    if (setIngestKbps) setIngestKbps(prev => mergeCarry(prev, kbpsMap));

    // SRS badge: single merged write per cycle.
    // 1. Active ingest PROVES SRS is listening (the camera is publishing into
    //    it on 1935 right now) — it overrides a failed probe.
    // 2. Flap damping: one missed probe keeps the previous value; only two
    //    consecutive misses (with no ingest evidence) flip the badge to red.
    //    A genuinely down instance still shows red within ~2 cycles.
    setRtmpOk(prev => {
      const next = {};
      for (const inst of instances) {
        const id = inst.instanceId;
        const up = rtmpProbe[id] === true || ingestMap[id] === true;
        if (up) {
          rtmpFailStreakRef.current[id] = 0;
          next[id] = true;
        } else {
          const misses = (rtmpFailStreakRef.current[id] || 0) + 1;
          rtmpFailStreakRef.current[id] = misses;
          next[id] = misses < 2 ? (prev[id] ?? false) : false;
        }
      }
      return next;
    });

    if (setSourceOverrideMap) {
      const soMap = {};
      for (const [id, mon] of monitorResults) {
        if (mon) {
          soMap[id] = {
            forcedSource: mon.drift?.forced_source ?? null,
            activeSource: mon.proxy?.active_source ?? mon.drift?.active_source ?? null,
          };
        }
      }
      if (Object.keys(soMap).length) setSourceOverrideMap(prev => ({ ...prev, ...soMap }));
    }

    // Terminated instances detected → ask the page to reload its list, at most
    // once every 30s (the reload itself takes a moment; without the throttle
    // every 2s poll would queue another one).
    if (goneIds.size && onInstanceGone) {
      const nowTs = Date.now();
      if (nowTs - (goneRefreshAtRef.current || 0) > 30000) {
        goneRefreshAtRef.current = nowTs;
        console.log(`[useStatuses] ${goneIds.size} terminated instance(s) still in list — refreshing`);
        try { onInstanceGone([...goneIds]); } catch {}
      }
    }
  }, [setRtmpOk, setIngestOk, setFfmpegStat, setSpeedMap, setIngestKbps, setSourceOverrideMap, checkRtmp, onInstanceGone]);

  return { refreshStatuses };
}
